import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useVenue } from "../VenueProvider";
import { Badge } from "../badge";
import { BanknotesIcon, ArrowTrendingDownIcon, CalendarDaysIcon, EyeIcon } from "@heroicons/react/20/solid";
import { formatCalendarDate } from "../../utils/dates";
import { normalizeDepositoEstado } from "../../utils/depositoEstado";
import { ACCENT, formatMXN, Section } from "./shared";
import ArchivoPreviewModal from "../shared/ArchivoPreviewModal";
import { useAuth } from "../AuthProvider";
import { esSoloLectura } from "../../utils/roles";

const CuentasDepositos = () => {
  const navigate = useNavigate();
  const {
    selectedVenueId,
    events: allEvents,
    calendarTimezone,
    clienteDepositos,
    clienteDepositosLoading,
    loadProveedorDepositos,
    loadEventArchivos,
  } = useVenue();

  const [proveedorDepositos, setProveedorDepositos] = useState([]);
  const [proveedorDepositosLoading, setProveedorDepositosLoading] = useState(false);
  const [depositoFilterEstado, setDepositoFilterEstado] = useState("");
  const [clienteArchivos, setClienteArchivos] = useState({});
  const [previewArchivo, setPreviewArchivo] = useState(null);
  // Solo lectura (P-FRA-13): se ve que hay comprobante, pero no se abre.
  const { userInfo } = useAuth();
  const soloLectura = esSoloLectura(userInfo);

  useEffect(() => {
    if (!selectedVenueId) return;
    let cancelled = false;
    setProveedorDepositosLoading(true);
    loadProveedorDepositos()
      .then((data) => {
        if (cancelled) return;
        setProveedorDepositos(
          data.map((d) => ({ ...d, estado: normalizeDepositoEstado(d.estado) }))
        );
        setProveedorDepositosLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setProveedorDepositos([]);
        setProveedorDepositosLoading(false);
      });
    return () => { cancelled = true; };
  }, [selectedVenueId, loadProveedorDepositos]);

  const eventById = useMemo(() => {
    const m = {};
    allEvents.forEach((e) => { m[e.id] = e; });
    return m;
  }, [allEvents]);

  // Comprobantes de cliente depositos viven en events/{eid}/archivos con
  // linkedTo.kind="deposito". Cargamos archivos de los eventos que tienen
  // depositos y los indexamos por linkedTo.id para el lookup de la fila.
  const clienteEventIds = useMemo(() => {
    return [...new Set(clienteDepositos.map((d) => d.eventId).filter(Boolean))];
  }, [clienteDepositos]);

  useEffect(() => {
    if (!loadEventArchivos || clienteEventIds.length === 0) {
      setClienteArchivos({});
      return;
    }
    let cancelled = false;
    Promise.all(clienteEventIds.map((eid) => loadEventArchivos("default", eid)))
      .then((results) => {
        if (cancelled) return;
        const idx = {};
        for (const archivos of results) {
          for (const a of archivos || []) {
            if (a?.linkedTo?.kind === "deposito" && a?.linkedTo?.id) {
              const id = a.linkedTo.id;
              const role = a.linkedRole === "devolucion" ? "devolucion" : "pago";
              if (!idx[id]) idx[id] = {};
              if (!idx[id][role]) idx[id][role] = a;
            }
          }
        }
        setClienteArchivos(idx);
      })
      .catch((err) => {
        if (!cancelled) console.error("Error loading cliente deposito archivos:", err);
      });
    return () => { cancelled = true; };
  }, [loadEventArchivos, clienteEventIds]);

  const depositoSummary = useMemo(() => {
    // En estado "devuelto" con parcial: la porción devuelta cuenta en devueltos
    // (count del doc) y el resto (monto - montoDevuelto) cuenta en aplicados.total
    // como "parcial". El count de aplicados es solo de docs estrictamente
    // aplicados; los parciales se cuentan por separado.
    const tally = (arr) => {
      const r = {
        enCustodia: { total: 0, count: 0 },
        devueltos: { total: 0, count: 0 },
        aplicados: { total: 0, count: 0, parciales: 0 },
      };
      arr.forEach((d) => {
        const monto = Number(d.monto) || 0;
        if (d.estado === "activo") {
          r.enCustodia.total += monto;
          r.enCustodia.count += 1;
        } else if (d.estado === "devuelto") {
          const devuelto = Number(d.montoDevuelto ?? d.monto) || 0;
          r.devueltos.total += devuelto;
          r.devueltos.count += 1;
          const aplicadoParcial = Math.max(0, monto - devuelto);
          if (aplicadoParcial > 0) {
            r.aplicados.total += aplicadoParcial;
            r.aplicados.parciales += 1;
          }
        } else if (d.estado === "aplicado") {
          r.aplicados.total += monto;
          r.aplicados.count += 1;
        }
      });
      return r;
    };
    const merge = (a, b) => ({
      total: a.total + b.total,
      count: a.count + b.count,
      parciales: (a.parciales || 0) + (b.parciales || 0),
    });
    const c = tally(clienteDepositos);
    const p = tally(proveedorDepositos);
    return {
      enCustodia: merge(c.enCustodia, p.enCustodia),
      devueltos: merge(c.devueltos, p.devueltos),
      aplicados: merge(c.aplicados, p.aplicados),
    };
  }, [clienteDepositos, proveedorDepositos]);

  const filterDepositos = useCallback((arr) => {
    if (!depositoFilterEstado) return arr;
    return arr.filter((d) => d.estado === depositoFilterEstado);
  }, [depositoFilterEstado]);

  return (
    <div className="p-4">
      {/* TL;DR hero */}
      <div
        className="rounded-xl p-5 mb-4 text-white shadow-sm"
        style={{ backgroundColor: ACCENT }}
      >
        <div className="flex items-center gap-2 mb-1">
          <BanknotesIcon className="h-5 w-5" />
          <span className="text-xs font-semibold uppercase tracking-wide opacity-90">
            Total en custodia
          </span>
        </div>
        <div className="text-3xl font-bold">
          {formatMXN(depositoSummary.enCustodia.total)}
        </div>
        <div className="text-sm opacity-90 mt-1">
          {depositoSummary.enCustodia.count}{" "}
          {depositoSummary.enCustodia.count === 1 ? "depósito activo" : "depósitos activos"}{" "}
          (cliente + proveedor) — dinero que aún retienes
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
        <Section icon={CalendarDaysIcon} title="Devueltos">
          <div className="text-2xl font-bold text-emerald-600">
            {formatMXN(depositoSummary.devueltos.total)}
          </div>
          <div className="text-sm text-zinc-500 mt-1">
            {depositoSummary.devueltos.count}{" "}
            {depositoSummary.devueltos.count === 1 ? "depósito devuelto" : "depósitos devueltos"}
          </div>
        </Section>
        <Section icon={ArrowTrendingDownIcon} title="Aplicados">
          <div className="text-2xl font-bold text-red-600">
            {formatMXN(depositoSummary.aplicados.total)}
          </div>
          <div className="text-sm text-zinc-500 mt-1">
            {(() => {
              const { count, parciales } = depositoSummary.aplicados;
              const parts = [];
              if (count > 0) {
                parts.push(`${count} ${count === 1 ? "aplicado" : "aplicados"}`);
              }
              if (parciales > 0) {
                parts.push(`${parciales} ${parciales === 1 ? "parcial" : "parciales"}`);
              }
              if (parts.length === 0) return "0 depósitos aplicados por daños / penalización";
              return `${parts.join(" + ")} · por daños / penalización`;
            })()}
          </div>
        </Section>
      </div>

      {/* Estado chips */}
      <div className="flex flex-wrap items-center gap-2 mb-4 px-1">
        <span className="text-xs font-medium text-zinc-500 dark:text-zinc-400 mr-1">
          Mostrar:
        </span>
        {[
          {
            value: "",
            label: "Todos",
            count: depositoSummary.enCustodia.count + depositoSummary.devueltos.count + depositoSummary.aplicados.count,
            activeBg: "#52525B",
          },
          { value: "activo", label: "En custodia", count: depositoSummary.enCustodia.count, activeBg: ACCENT },
          { value: "devuelto", label: "Devueltos", count: depositoSummary.devueltos.count, activeBg: "#059669" },
          { value: "aplicado", label: "Aplicados", count: depositoSummary.aplicados.count, activeBg: "#DC2626" },
        ].map((opt) => {
          const active = depositoFilterEstado === opt.value;
          return (
            <button
              key={opt.value || "todos"}
              type="button"
              onClick={() => setDepositoFilterEstado(opt.value)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold transition ${
                active
                  ? "text-white shadow-sm"
                  : "text-zinc-600 dark:text-zinc-300 bg-zinc-100 dark:bg-zinc-800 hover:bg-zinc-200 dark:hover:bg-zinc-700"
              }`}
              style={active ? { backgroundColor: opt.activeBg } : undefined}
            >
              {opt.label}
              <span className={`ml-1.5 ${active ? "opacity-90" : "text-zinc-400"}`}>
                ({opt.count})
              </span>
            </button>
          );
        })}
      </div>

      {/* Por evento (cliente) */}
      <div className="mb-6">
        <h3 className="text-sm font-semibold text-zinc-700 dark:text-zinc-300 mb-2 px-1">
          Por evento (cliente)
        </h3>
        {clienteDepositosLoading ? (
          <div className="text-center py-8 text-zinc-400 text-sm">Cargando...</div>
        ) : (() => {
          const rows = filterDepositos(clienteDepositos);
          if (rows.length === 0) {
            return (
              <div className="text-center py-8 text-zinc-400 text-sm italic">
                No hay depósitos de cliente {depositoFilterEstado ? `con estado "${depositoFilterEstado}"` : ""}.
              </div>
            );
          }
          return (
            <div className="overflow-x-auto rounded-lg border border-zinc-200 dark:border-zinc-700/60">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="bg-zinc-50 dark:bg-zinc-800/40">
                    <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-zinc-500">Evento</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-zinc-500">Fecha</th>
                    <th className="px-3 py-2 text-right text-xs font-semibold uppercase tracking-wide text-zinc-500">Monto</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-zinc-500">Estado</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-zinc-500">Devolución / Aplicación</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-zinc-500">Notas</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-zinc-500 w-12">Comp.</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-100 dark:divide-zinc-700/40">
                  {rows.map((d) => {
                    const event = eventById[d.eventId];
                    const badgeColor = d.estado === "activo" ? "amber" : d.estado === "devuelto" ? "emerald" : "red";
                    const badgeLabel = d.estado === "activo" ? "En custodia" : d.estado === "devuelto" ? "Devuelto" : "Aplicado";
                    const cierreFecha = formatCalendarDate(d.fechaDevolucion, calendarTimezone);
                    const partial = d.estado === "devuelto" && Number(d.montoDevuelto) < Number(d.monto);
                    return (
                      <tr key={d.id}>
                        <td className="px-3 py-2.5">
                          {event ? (
                            <button
                              type="button"
                              onClick={() =>
                                navigate(`/dashboard/eventos/${event.id}?tab=depositos`, {
                                  state: { from: "cuentas-depositos" },
                                })
                              }
                              className="block max-w-[240px] truncate text-left text-sm font-medium hover:underline"
                              style={{ color: ACCENT }}
                              title={event.nombre || "Evento"}
                            >
                              {event.nombre || "Evento"}
                            </button>
                          ) : (
                            <span className="text-zinc-400">—</span>
                          )}
                        </td>
                        <td className="px-3 py-2.5 whitespace-nowrap text-zinc-700 dark:text-zinc-300">
                          {formatCalendarDate(d.fecha, calendarTimezone) || "-"}
                        </td>
                        <td className="px-3 py-2.5 text-right whitespace-nowrap font-medium text-zinc-900 dark:text-white">
                          {formatMXN(d.monto)}
                        </td>
                        <td className="px-3 py-2.5">
                          <Badge color={badgeColor}>{badgeLabel}</Badge>
                        </td>
                        <td className="px-3 py-2.5 text-xs text-zinc-600 dark:text-zinc-400">
                          {d.estado === "activo" ? (
                            <span className="text-zinc-400">—</span>
                          ) : (
                            <div className="space-y-0.5">
                              {cierreFecha && <div>{cierreFecha}</div>}
                              {d.estado === "devuelto" && (
                                <div className="text-emerald-700 font-medium">
                                  Devuelto: {formatMXN(d.montoDevuelto ?? d.monto)}
                                  {partial && <span className="ml-1 text-amber-600">(parcial)</span>}
                                </div>
                              )}
                              {d.estado === "aplicado" && (
                                <div className="text-red-700 font-medium">Aplicado por venue</div>
                              )}
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-2.5 text-zinc-600 dark:text-zinc-400 text-xs max-w-[240px] break-words">
                          {d.notas || <span className="text-zinc-400">—</span>}
                        </td>
                        <td className="px-3 py-2.5">
                          {(() => {
                            const set = clienteArchivos[d.id] || {};
                            const pago = set.pago;
                            const dev = set.devolucion;
                            if (!pago && !dev) {
                              return <span className="text-zinc-300 text-xs">—</span>;
                            }
                            return (
                              <div className="inline-flex items-center gap-1">
                                {pago && (
                                  <button
                                    type="button"
                                    onClick={() => setPreviewArchivo(pago)}
                                    disabled={soloLectura}
                                    title={soloLectura ? "Comprobante adjunto" : "Ver comprobante del pago"}
                                    aria-label="Ver comprobante del pago"
                                    className="inline-flex h-7 w-7 items-center justify-center rounded transition-colors hover:bg-zinc-100 disabled:cursor-default disabled:opacity-50 disabled:hover:bg-transparent"
                                    style={{ color: ACCENT }}
                                  >
                                    <EyeIcon className="h-4 w-4" />
                                  </button>
                                )}
                                {dev && (
                                  <button
                                    type="button"
                                    onClick={() => setPreviewArchivo(dev)}
                                    disabled={soloLectura}
                                    title={soloLectura ? "Comprobante adjunto" : "Ver comprobante de devolución"}
                                    aria-label="Ver comprobante de devolución"
                                    className="inline-flex h-7 w-7 items-center justify-center rounded transition-colors hover:bg-emerald-50 disabled:cursor-default disabled:opacity-50 disabled:hover:bg-transparent"
                                    style={{ color: "#059669" }}
                                  >
                                    <EyeIcon className="h-4 w-4" />
                                  </button>
                                )}
                              </div>
                            );
                          })()}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          );
        })()}
      </div>

      {/* Por proveedor */}
      <div>
        <h3 className="text-sm font-semibold text-zinc-700 dark:text-zinc-300 mb-2 px-1">
          Por proveedor
        </h3>
        {proveedorDepositosLoading ? (
          <div className="text-center py-8 text-zinc-400 text-sm">Cargando...</div>
        ) : (() => {
          const rows = filterDepositos(proveedorDepositos);
          if (rows.length === 0) {
            return (
              <div className="text-center py-8 text-zinc-400 text-sm italic">
                No hay depósitos de proveedor {depositoFilterEstado ? `con estado "${depositoFilterEstado}"` : ""}.
              </div>
            );
          }
          return (
            <div className="overflow-x-auto rounded-lg border border-zinc-200 dark:border-zinc-700/60">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="bg-zinc-50 dark:bg-zinc-800/40">
                    <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-zinc-500">Proveedor</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-zinc-500">Fecha</th>
                    <th className="px-3 py-2 text-right text-xs font-semibold uppercase tracking-wide text-zinc-500">Monto</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-zinc-500">Estado</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-zinc-500">Devolución / Aplicación</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-zinc-500">Notas</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-zinc-500 w-12">Comp.</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-100 dark:divide-zinc-700/40">
                  {rows.map((d) => {
                    const badgeColor = d.estado === "activo" ? "amber" : d.estado === "devuelto" ? "emerald" : "red";
                    const badgeLabel = d.estado === "activo" ? "En custodia" : d.estado === "devuelto" ? "Devuelto" : "Aplicado";
                    const cierreFecha = formatCalendarDate(d.fechaDevolucion, calendarTimezone);
                    const partial = d.estado === "devuelto" && Number(d.montoDevuelto) < Number(d.monto);
                    const comprobantePago = d.comprobantePago || d.comprobante || null;
                    const comprobanteDevolucion = d.comprobanteDevolucion || null;
                    return (
                      <tr key={d.id}>
                        <td className="px-3 py-2.5">
                          <button
                            type="button"
                            onClick={() =>
                              navigate(`/dashboard/proveedores/${d.proveedorId}?tab=depositos`, {
                                state: { from: "cuentas-depositos" },
                              })
                            }
                            className="block max-w-[240px] truncate text-left text-sm font-medium hover:underline"
                            style={{ color: ACCENT }}
                            title={d.proveedorNombre || "Proveedor"}
                          >
                            {d.proveedorNombre || "Proveedor"}
                          </button>
                        </td>
                        <td className="px-3 py-2.5 whitespace-nowrap text-zinc-700 dark:text-zinc-300">
                          {formatCalendarDate(d.fecha, calendarTimezone) || "-"}
                        </td>
                        <td className="px-3 py-2.5 text-right whitespace-nowrap font-medium text-zinc-900 dark:text-white">
                          {formatMXN(d.monto)}
                        </td>
                        <td className="px-3 py-2.5">
                          <Badge color={badgeColor}>{badgeLabel}</Badge>
                        </td>
                        <td className="px-3 py-2.5 text-xs text-zinc-600 dark:text-zinc-400">
                          {d.estado === "activo" ? (
                            <span className="text-zinc-400">—</span>
                          ) : (
                            <div className="space-y-0.5">
                              {cierreFecha && <div>{cierreFecha}</div>}
                              {d.estado === "devuelto" && (
                                <div className="text-emerald-700 font-medium">
                                  Devuelto: {formatMXN(d.montoDevuelto ?? d.monto)}
                                  {partial && <span className="ml-1 text-amber-600">(parcial)</span>}
                                </div>
                              )}
                              {d.estado === "aplicado" && (
                                <div className="text-red-700 font-medium">Aplicado por venue</div>
                              )}
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-2.5 text-zinc-600 dark:text-zinc-400 text-xs max-w-[240px] break-words">
                          {d.descripcion || <span className="text-zinc-400">—</span>}
                        </td>
                        <td className="px-3 py-2.5">
                          {comprobantePago || comprobanteDevolucion ? (
                            <div className="inline-flex items-center gap-1">
                              {comprobantePago && (
                                <button
                                  type="button"
                                  onClick={() => setPreviewArchivo(comprobantePago)}
                                  disabled={soloLectura}
                                  title={soloLectura ? "Comprobante adjunto" : "Ver comprobante del pago"}
                                  aria-label="Ver comprobante del pago"
                                  className="inline-flex h-7 w-7 items-center justify-center rounded transition-colors hover:bg-zinc-100 disabled:cursor-default disabled:opacity-50 disabled:hover:bg-transparent"
                                  style={{ color: ACCENT }}
                                >
                                  <EyeIcon className="h-4 w-4" />
                                </button>
                              )}
                              {comprobanteDevolucion && (
                                <button
                                  type="button"
                                  onClick={() => setPreviewArchivo(comprobanteDevolucion)}
                                  disabled={soloLectura}
                                  title={soloLectura ? "Comprobante adjunto" : "Ver comprobante de devolución"}
                                  aria-label="Ver comprobante de devolución"
                                  className="inline-flex h-7 w-7 items-center justify-center rounded transition-colors hover:bg-emerald-50 disabled:cursor-default disabled:opacity-50 disabled:hover:bg-transparent"
                                  style={{ color: "#059669" }}
                                >
                                  <EyeIcon className="h-4 w-4" />
                                </button>
                              )}
                            </div>
                          ) : (
                            <span className="text-zinc-300 text-xs">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          );
        })()}
      </div>
      <ArchivoPreviewModal
        open={!!previewArchivo && !soloLectura}
        onClose={() => setPreviewArchivo(null)}
        storagePath={previewArchivo?.storagePath}
        filename={previewArchivo?.filename}
        mimeType={previewArchivo?.mimeType}
      />
    </div>
  );
};

export default CuentasDepositos;
